import React, { useState } from "react";
import { Plus, Edit, Trash2, GripVertical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { TourPackage, useTourPackages } from "@/hooks/useTourPackages";
import { useToast } from "@/hooks/use-toast";

interface PackageManagerProps {
  tourId: string;
  onMinPriceChange?: (minPrice: number) => void;
}

interface PackageFormData {
  package_name: string;
  package_price: string;
  persons_included: string;
  description: string;
}

export const PackageManager: React.FC<PackageManagerProps> = ({ tourId, onMinPriceChange }) => {
  const { packages, createPackage, updatePackage, deletePackage } = useTourPackages(tourId);
  const { toast } = useToast();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState<TourPackage | null>(null);
  const [formData, setFormData] = useState<PackageFormData>({
    package_name: "",
    package_price: "",
    persons_included: "",
    description: ""
  });

  const resetForm = () => {
    setFormData({
      package_name: "",
      package_price: "",
      persons_included: "",
      description: ""
    });
    setEditingPackage(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const packageData = {
        tour_id: tourId,
        package_name: formData.package_name,
        package_price: parseFloat(formData.package_price),
        persons_included: parseInt(formData.persons_included),
        description: formData.description || null,
        is_active: true,
        sort_order: packages.length
      };

      if (editingPackage) {
        await updatePackage(editingPackage.id, packageData);
        toast({
          title: "Package Updated",
          description: "The package has been updated successfully.",
        });
      } else {
        await createPackage(packageData);
        toast({
          title: "Package Created",
          description: "The package has been created successfully.",
        });
      }

      // Update min price in parent component
      if (onMinPriceChange) {
        const minPrice = Math.min(...packages.map(pkg => pkg.package_price), packageData.package_price);
        onMinPriceChange(minPrice);
      }

      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      toast({
        title: "Error",
        description: editingPackage ? "Failed to update package" : "Failed to create package",
        variant: "destructive",
      });
    }
  };

  const handleEdit = (pkg: TourPackage) => {
    setEditingPackage(pkg);
    setFormData({
      package_name: pkg.package_name,
      package_price: pkg.package_price.toString(),
      persons_included: pkg.persons_included.toString(),
      description: pkg.description || ""
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm("Are you sure you want to delete this package?")) {
      try {
        await deletePackage(id);
        toast({
          title: "Package Deleted",
          description: "The package has been deleted successfully.",
        });

        // Update min price in parent component
        if (onMinPriceChange && packages.length > 1) {
          const remainingPackages = packages.filter(pkg => pkg.id !== id);
          if (remainingPackages.length > 0) {
            const minPrice = Math.min(...remainingPackages.map(pkg => pkg.package_price));
            onMinPriceChange(minPrice);
          } else {
            onMinPriceChange(0);
          }
        }
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to delete package",
          variant: "destructive",
        });
      }
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Tour Packages</h3>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm}>
              <Plus className="w-4 h-4 mr-2" />
              Add Package
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {editingPackage ? "Edit Package" : "Add New Package"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="package_name">Package Name</Label>
                <Input
                  id="package_name"
                  value={formData.package_name}
                  onChange={(e) => setFormData({ ...formData, package_name: e.target.value })}
                  placeholder="e.g., Family Package"
                  required
                />
              </div>
              <div>
                <Label htmlFor="package_price">Price ($)</Label>
                <Input
                  id="package_price"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.package_price}
                  onChange={(e) => setFormData({ ...formData, package_price: e.target.value })}
                  placeholder="0.00"
                  required
                />
              </div>
              <div>
                <Label htmlFor="persons_included">Persons Included</Label>
                <Input
                  id="persons_included"
                  type="number"
                  min="1"
                  value={formData.persons_included}
                  onChange={(e) => setFormData({ ...formData, persons_included: e.target.value })}
                  placeholder="2"
                  required
                />
              </div>
              <div>
                <Label htmlFor="description">Description (Optional)</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Package description..."
                  rows={3}
                />
              </div>
              <div className="flex gap-2">
                <Button type="submit" className="flex-1">
                  {editingPackage ? "Update Package" : "Create Package"}
                </Button>
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-3">
        {packages.length === 0 ? (
          <Card>
            <CardContent className="p-6 text-center text-muted-foreground">
              No packages created yet. Add your first package to get started.
            </CardContent>
          </Card>
        ) : (
          packages.map((pkg) => (
            <Card key={pkg.id}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <GripVertical className="w-4 h-4 text-muted-foreground" />
                    <div>
                      <h4 className="font-semibold">{pkg.package_name}</h4>
                      <p className="text-sm text-muted-foreground">
                        ${pkg.package_price} • {pkg.persons_included} persons
                      </p>
                      {pkg.description && (
                        <p className="text-sm text-muted-foreground mt-1">{pkg.description}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(pkg)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(pkg.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};